import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../../pages-styles/project-consultancy.css';
import { usePageMeta } from '../../hooks/usePageMeta';

export default function Discretion() {
  usePageMeta('Discretion & Limitations - Magbity Agency Ltd', 'Our approach to discretion, acceptance criteria, and service limitations.');

  useEffect(() => {
    const el = document.querySelector('.disc-3d-card');
    if (el) el.classList.add('enter');
  }, []);

  return (
    <div className="project-consultancy">
      <section className="consultancy-hero-banner disc-hero">
        <div className="consultancy-hero-overlay"></div>
        <div className="consultancy-hero-content">
          <div className="consultancy-hero-text">
            <h1>Discretion & Service Limitations</h1>
            <p>
              We reserve the right to accept or decline projects based on capacity, fit, legal or ethical considerations, and
              overall project viability. Discretion helps protect client and firm interests.
            </p>
          </div>

          <div className="disc-illustration">
            <div className="disc-3d-scene">
              <div className="disc-3d-card">
                <div className="face front">
                  <svg width="220" height="220" viewBox="0 0 220 220" xmlns="http://www.w3.org/2000/svg">
                    <rect x="14" y="14" width="192" height="192" rx="12" fill="#f8fafc" />
                    <g transform="translate(50,48)">
                      <path d="M0 0h80v20H0z" fill="#16c784" opacity="0.12" />
                      <path d="M0 30h80v10H0z" fill="#00d4ff" opacity="0.08" />
                    </g>
                  </svg>
                  <div className="card-label">Discretionary Acceptance</div>
                </div>
                <div className="face back">
                  <h4>Selective Engagement</h4>
                  <p>We prioritise fit, ethics and capacity when accepting work.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="consultancy-services disc-details">
        <h2>Our Discretion & Limits</h2>
        <div className="services-list">
          <div className="service-item left">
            <div className="service-content">
              <h3>Right to Decline</h3>
              <p>We may decline projects that conflict with our ethics, capacity, or strategic focus.</p>
              <ul className="service-details-list">
                <li>Ethical conflicts lead to decline</li>
                <li>Capacity constraints are considered</li>
                <li>Legal/regulatory risks are assessed</li>
                <li>Reputational considerations matter</li>
                <li>Client-fit assessment guides decisions</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 5h22v2H1z" opacity=".2"/>
                <path d="M1 5h22v2H1z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2l4 4H8l4-4z" opacity=".2"/>
                <path d="M12 2l4 4H8l4-4z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>Selective Client Fit</h3>
              <p>We assess alignment with our expertise and the likelihood of creating value for the client.</p>
              <ul className="service-details-list">
                <li>Strategic alignment evaluated</li>
                <li>Resource commitment reviewed</li>
                <li>Legal and compliance fit checked</li>
                <li>Expected ROI and timescales assessed</li>
                <li>Mutual expectations clarified upfront</li>
              </ul>
            </div>
          </div>

          <div className="service-item left">
            <div className="service-content">
              <h3>Scope Limitations</h3>
              <p>We define clear scopes; work outside scope requires agreement and potential re-scoping fees.</p>
              <ul className="service-details-list">
                <li>Change requests subject to review</li>
                <li>Additional work requires new terms</li>
                <li>Third-party approvals may be necessary</li>
                <li>Timeboxed engagements preferred</li>
                <li>Deliverables clearly specified</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 3h18v2H3z" opacity=".2"/>
                <path d="M3 3h18v2H3z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2a10 10 0 100 20 10 10 0 000-20z" opacity=".2"/>
                <path d="M12 2a10 10 0 100 20 10 10 0 000-20z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>Project Viability Assessment</h3>
              <p>We perform viability reviews and may decline projects not meeting minimum thresholds.</p>
              <ul className="service-details-list">
                <li>Feasibility and risk checks</li>
                <li>Minimum viability criteria applied</li>
                <li>Resource and timeline realism considered</li>
                <li>Requires client cooperation for accurate assessment</li>
                <li>Results guide acceptance decisions</li>
              </ul>
            </div>
          </div>

          <div className="service-item left">
            <div className="service-content">
              <h3>Client Suitability</h3>
              <p>We look for clients committed to implementing recommendations and collaborating effectively.</p>
              <ul className="service-details-list">
                <li>Decision-makers must be engaged</li>
                <li>Resource availability is necessary</li>
                <li>Culture and governance are considered</li>
                <li>Shared commitment to outcomes required</li>
                <li>Capacity to act on recommendations matters</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12h14v2H5z" opacity=".2"/>
                <path d="M5 12h14v2H5z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2l8 4v8l-8 4-8-4V6z" opacity=".2"/>
                <path d="M12 2l8 4v8l-8 4-8-4V6z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>Important Notes</h3>
              <p>Please review our engagement terms; acceptance is subject to review and contract.</p>
              <ul className="service-details-list">
                <li>Acceptance subject to contract</li>
                <li>Initial assessments may be required</li>
                <li>Confidentiality agreements can be requested</li>
                <li>We may request references or background info</li>
                <li>Formal engagements supersede preliminary discussions</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="responsibility-checklist">
        <h2>Next Steps for Prospective Clients</h2>
        <div className="checklist-grid">
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>Initial Brief</h4>
            <p>Share a concise project brief for assessment.</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>Preliminary Call</h4>
            <p>We may conduct a short call to clarify fit.</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>Feasibility Review</h4>
            <p>We'll review viability and resource needs.</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>Agreement</h4>
            <p>If suitable, we propose terms and scope for engagement.</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>Onboarding</h4>
            <p>Onboarding documents and access requests will be issued.</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>Kickoff</h4>
            <p>We schedule a kickoff to begin work after agreement.</p>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <h2>Want to Discuss a Project?</h2>
        <p>If you'd like to explore fit, share a brief and we'll respond with next steps.</p>
        <Link to="/contact" className="cta-button">Get in Touch</Link>
      </section>
    </div>
  );
}
